import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, clearToken, type DeliveryItem } from '../api';

type Tab = 'pending' | 'history';

export default function DeliveryListPage(): JSX.Element {
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('pending');
  const [items, setItems] = useState<DeliveryItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Filtros (solo aplican en Historial)
  const [searchInput, setSearchInput] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Debounce del input de búsqueda para no disparar query por cada tecla.
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Carga de datos según tab + filtros
  useEffect(() => {
    setItems(null);
    setErr(null);
    const params =
      tab === 'pending'
        ? { status: 'pending' as const }
        : {
            status: 'signed' as const,
            search: searchDebounced || undefined,
            from: from || undefined,
            to: to || undefined,
          };
    api
      .list(params)
      .then(setItems)
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        setErr(msg);
        if (msg.toLowerCase().includes('token')) {
          clearToken();
          nav('/login');
        }
      });
  }, [tab, searchDebounced, from, to, nav]);

  function logout(): void {
    clearToken();
    nav('/login');
  }

  function clearFilters(): void {
    setSearchInput('');
    setFrom('');
    setTo('');
  }

  const hasFilters = Boolean(searchDebounced || from || to);

  return (
    <>
      <div className="header">
        Entregas
        <div className="sub">
          <a
            href="#"
            style={{ color: 'white', textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault();
              logout();
            }}
          >
            Salir
          </a>
        </div>
      </div>

      <div className="container">
        <div className="tabs">
          <button
            className={`tab ${tab === 'pending' ? 'active' : ''}`}
            onClick={() => setTab('pending')}
          >
            Pendientes
          </button>
          <button
            className={`tab ${tab === 'history' ? 'active' : ''}`}
            onClick={() => setTab('history')}
          >
            Historial
          </button>
        </div>

        {tab === 'history' && (
          <div className="filters">
            <input
              className="input"
              placeholder="Buscar por factura o cliente…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <div className="row">
              <div>
                <div className="label">Desde</div>
                <input
                  className="input"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div>
                <div className="label">Hasta</div>
                <input
                  className="input"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>
            {hasFilters && (
              <button className="btn secondary" onClick={clearFilters} style={{ width: '100%' }}>
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        {err && <div className="alert error">{err}</div>}
        {!items && !err && <div className="empty">Cargando…</div>}
        {items && items.length === 0 && (
          <div className="empty">
            {tab === 'pending'
              ? 'No tenés entregas pendientes 🎉'
              : hasFilters
                ? 'No hay resultados con esos filtros.'
                : 'Todavía no firmaste ninguna entrega.'}
          </div>
        )}
        {items && items.map((d) => <DeliveryCard key={d.id} item={d} tab={tab} />)}
      </div>
    </>
  );
}

function DeliveryCard({ item, tab }: { item: DeliveryItem; tab: Tab }): JSX.Element {
  const signedDate = useMemo(() => formatDateTime(item.signed_at), [item.signed_at]);
  const createdDate = useMemo(() => formatDate(item.created_at), [item.created_at]);

  // Link click-to-chat de WhatsApp (con el mensaje según la plantilla del cliente, armado en el server).
  const waUrl = item.whatsapp_url;
  const mapsUrl =
    item.signed_lat && item.signed_lng
      ? `https://www.google.com/maps?q=${item.signed_lat},${item.signed_lng}`
      : null;

  return (
    <div className="card">
      <h3>Factura {item.numero_factura}</h3>
      <div className="meta">
        {item.destinatario_nombre ?? 'Sin nombre'}
        {item.destinatario_phone ? ` · ${item.destinatario_phone}` : ''}
      </div>

      {tab === 'pending' && (
        <div className="meta small">Creada: {createdDate}</div>
      )}

      {tab === 'history' && (
        <>
          <div className="meta small">Firmada: {signedDate ?? '—'}</div>
          {mapsUrl && (
            <div className="meta small">
              <a href={mapsUrl} target="_blank" rel="noreferrer">
                Ver en Maps
              </a>
            </div>
          )}
        </>
      )}

      <div className="row">
        {tab === 'pending' ? (
          <>
            <a className="btn secondary" href={item.pdf_url} target="_blank" rel="noreferrer">
              Ver PDF
            </a>
            {waUrl && (
              <a className="btn secondary" href={waUrl} target="_blank" rel="noreferrer">
                WhatsApp
              </a>
            )}
            <Link className="btn" to={`/sign/${item.id}`}>
              Firmar
            </Link>
          </>
        ) : (
          <>
            <a className="btn secondary" href={item.pdf_url} target="_blank" rel="noreferrer">
              Original
            </a>
            {item.signed_url ? (
              <a className="btn" href={item.signed_url} target="_blank" rel="noreferrer">
                Firmado
              </a>
            ) : (
              <button className="btn" disabled>
                Sin firmar
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
